import type { SlotSymbolDef } from '../../widgets/slot/types';
import type { WheelSegment } from '../../widgets/wheel/types';
import type { Theme } from '../Theme';

const SYMBOLS: readonly SlotSymbolDef[] = [
  { id: 'cherry', glyph: '🍒', tint: 0xff3b6b, weight: 5 },
  { id: 'lemon', glyph: '🍋', tint: 0xffd23b, weight: 5 },
  { id: 'orange', glyph: '🍊', tint: 0xff8a3d, weight: 4 },
  { id: 'bell', glyph: '🔔', tint: 0xffcc4d, weight: 3 },
  { id: 'star', glyph: '⭐', tint: 0xfff59d, weight: 2 }, // wild
  { id: 'seven', glyph: '7️⃣', tint: 0xff5252, weight: 1 },
  { id: 'gem', glyph: '💎', tint: 0x42e2f5, weight: 1 },
  { id: 'scatter', glyph: '🎯', tint: 0x6b3d77, weight: 1 }, // scatter
  // Money symbol — modestly rare (~6% per cell at weight 1.5 vs total
  // ~22.5), triggers hold-and-spin bonus when 4+ land in one spin
  // (~one bonus every 20–30 spins). Tint is gold to read as "treasure"
  // without competing with the wild's yellow. Demo-tuned; production
  // pulls weight down to ~0.6 paired with trigger threshold 6.
  { id: 'money', glyph: '💰', tint: 0xc89b1f, weight: 1.5, isMoney: true },
];

const SEGMENTS: readonly WheelSegment[] = [
  { id: 'x1-a', kind: 'multiplier', label: '×1', tint: 0xffd23b, multiplier: 1, weight: 4 },
  { id: 'x2-a', kind: 'multiplier', label: '×2', tint: 0x42e2f5, multiplier: 2, weight: 3 },
  { id: 'x0-a', kind: 'multiplier', label: '✕', tint: 0x4a2055, multiplier: 0, weight: 2 },
  { id: 'x3-a', kind: 'multiplier', label: '×3', tint: 0xff6bd6, multiplier: 3, weight: 2 },
  { id: 'x1-b', kind: 'multiplier', label: '×1', tint: 0xffd23b, multiplier: 1, weight: 3 },
  { id: 'x5-a', kind: 'multiplier', label: '×5', tint: 0xff8a3d, multiplier: 5, weight: 1 },
  { id: 'x0-b', kind: 'multiplier', label: '✕', tint: 0x4a2055, multiplier: 0, weight: 2 },
  { id: 'x10-a', kind: 'multiplier', label: '×10', tint: 0xfff59d, multiplier: 10, weight: 1 },
  { id: 'x2-b', kind: 'multiplier', label: '×2', tint: 0x42e2f5, multiplier: 2, weight: 3 },
  { id: 'x1-c', kind: 'multiplier', label: '×1', tint: 0xffd23b, multiplier: 1, weight: 3 },
  { id: 'x3-b', kind: 'multiplier', label: '×3', tint: 0xff6bd6, multiplier: 3, weight: 2 },
  { id: 'jackpot', kind: 'multiplier', label: '×25', tint: 0xff3b6b, multiplier: 25, weight: 1 },
];

/**
 * The original Good-time Casino theme — pink/purple chrome, fruit-and-gem
 * symbol roster, mixed-multiplier wheel with a ×25 jackpot. The default
 * theme is the "this is what every other theme has to clear the bar
 * against" reference; AI-generated themes are reviewed for visual quality
 * relative to this one before publish.
 */
export const DEFAULT_THEME: Theme = {
  id: 'default',
  name: 'Goodtime',
  palette: {
    accent: 0xff6bd6,         // brand pink
    secondary: 0x42e2f5,      // brand cyan
    warm: 0xfff59d,           // win-yellow
    frameFill: 0x1a0830,      // deep purple
    bgGradientTop: 0x140850,
    bgGradientBottom: 0x6c30a8,
    dimOverlay: 0x05010f,
  },
  slot: {
    symbols: SYMBOLS,
    wildSymbolId: 'star',
    scatterSymbolId: 'scatter',
  },
  wheel: {
    segments: SEGMENTS,
  },
};
